import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export function Panel({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn("border-bank-border bg-bank-surface rounded-lg border", className)}>{children}</div>
  );
}

/** A panel that names a section of a screen: "Upload a document", "Cost by stage". */
export function PanelHeader({ title, actions }: { title: ReactNode; actions?: ReactNode }) {
  return (
    <div className="border-bank-border flex items-center justify-between gap-3 border-b px-4 py-2.5">
      <h2 className="text-sm font-semibold">{title}</h2>
      {actions}
    </div>
  );
}

/**
 * A pane inside a screen that is already titled — the two halves of Review sit under
 * an h1 that says "Review queue", so "Waiting" and "Question #4192" are labels for
 * regions rather than headings competing with it. Mono uppercase on the inset
 * surface says structural, not editorial.
 */
export function PaneHeader({ title, actions }: { title: ReactNode; actions?: ReactNode }) {
  return (
    <div className="border-bank-border bg-bank-surface-2 flex items-center gap-3 border-b px-3.5 py-2">
      <h2 className="text-bank-muted truncate font-mono text-[11px] tracking-[0.07em] uppercase">{title}</h2>
      {actions ? <div className="ml-auto flex shrink-0 items-center gap-1.5">{actions}</div> : null}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="text-bank-muted px-4 py-8 text-center text-sm">{children}</p>;
}

/**
 * A message that needs to be read before the thing below it — a §7.2 quarantine
 * reason, a failed upload, a successful ingest. The left rule is what separates it
 * from a status chip: a chip labels a row, a callout addresses the reader.
 */
const TONES = {
  attention: "border-bank-attention bg-bank-attention-wash text-bank-attention-ink",
  good: "border-bank-good bg-bank-good-wash text-bank-good-ink",
  bad: "border-bank-bad bg-bank-bad-wash text-bank-bad-ink",
  // Context, not a warning: where a question came from, what happens next.
  info: "border-bank-accent bg-bank-accent-wash text-bank-accent-ink",
} as const;

export function Callout({
  tone = "attention",
  className,
  children,
}: {
  tone?: keyof typeof TONES;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("rounded-r-md border-l-2 px-3 py-2 text-sm", TONES[tone], className)}>{children}</div>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  // Never String() an unknown — "[object Object]" in a red box tells a reviewer
  // nothing, and the API's own message (§9: errors name the fix) is right there.
  const message =
    error instanceof Error ? error.message : typeof error === "string" ? error : JSON.stringify(error);
  // A 500 from the dev proxy carries no JSON body, so `problemMessage` returns the
  // empty string it was handed and this box renders as a bar of colour with nothing
  // in it. An error that says nothing is worse than no error at all.
  return <Callout tone="bad">{message.trim() || "The request failed and the API gave no reason."}</Callout>;
}
